import { FC, useCallback } from "react";
import { Banner, EditBanner } from "../../../types";
import { Button } from "../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Input, Label, Textarea, Checkbox } from "../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import { useStores } from "../../../hooks/useStores";
import { observer } from "mobx-react";
import { Col, Container, Row } from "../../common/Grid";
import { DatePicker } from "../../common/DatePicker";
import Feedback from "../../UI/Forms/FormElements/Feedback";
import { useDisclosure } from "react-use-disclosure";
import { FileUploadModal } from "../../common/FileUploadModal";

type Props = {
  banner?: Banner;
  isSubmitting: boolean;
  onSubmit: (values: EditBanner) => void;
  editable?: boolean;
  isAdmin?: boolean;
  validable: boolean;
  onCancel: () => void;
};

const BannerForm: FC<Props> = observer(
  ({
    banner,
    isSubmitting,
    onSubmit,
    editable = true,
    isAdmin = false,
    validable,
    onCancel,
  }) => {
    const { categoryStore } = useStores();
    const { t } = useTranslation();
    const {
      isOpen: isModalUploadOpen,
      open: openModalUpload,
      close: closeModalUpload,
    } = useDisclosure();

    const formik = useFormik({
      initialValues: {
        id: banner?.id || null,
        title: banner?.title || "",
        description: banner?.description || "",
        imageUrl: banner?.imageUrl || "",
        onClickUrl: banner?.onClickUrl || "",
        validFrom: banner?.validFrom ? new Date(banner?.validFrom) : undefined,
        validUntil: banner?.validUntil
          ? new Date(banner?.validUntil)
          : undefined,
        isVipOnly: banner?.isVipOnly || false,
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape({
        title: Yup.string().required(
          t("components.screenBanners.bannerForm.titleRequired")
        ),
        imageUrl: Yup.string().required(
          t("components.screenBanners.bannerForm.imageUrlRequired")
        ),
        onClickUrl: Yup.string().required(
          t("components.screenBanners.bannerForm.onClickUrlRequired")
        ),
        validFrom: Yup.string().required(
          t("components.screenBanners.bannerForm.validFromRequired")
        ),
        validUntil: Yup.string().required(
          t("components.screenBanners.bannerForm.validUntilRequired")
        ),
      }),
    });

    const handleUploadPoster = useCallback(
      async (imageUrls: string[]) => {
        formik.setFieldValue("imageUrl", imageUrls[0]);
        closeModalUpload();
      },
      [formik, closeModalUpload]
    );

    return (
      <form onSubmit={formik.handleSubmit}>
        <Container pb={"20px"}>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="title">
                  {t("components.screenBanners.bannerForm.title")}
                </Label>
                <Input
                  id="title"
                  name="title"
                  placeholder={t(
                    "components.screenBanners.bannerForm.titlePlaceholder"
                  )}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  state={formik.errors.title ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("title").error}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="description">
                  {t("components.screenBanners.bannerForm.description")}
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder={t(
                    "components.screenBanners.bannerForm.descriptionPlaceholder"
                  )}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  showState={true}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="imageUrl">
                  {t("components.screenBanners.bannerForm.imageUrl")}
                </Label>
                {formik.values.imageUrl.length > 0 ? (
                  <img
                    src={formik.values.imageUrl}
                    alt={formik.values.imageUrl}
                  />
                ) : (
                  <p>
                    {t(
                      "components.screenBanners.bannerForm.imageUrlPlaceholder"
                    )}
                  </p>
                )}
                <Button color="light" onClick={openModalUpload}>
                  {t("common.upload")}
                </Button>
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="onClickUrl">
                  {t("components.screenBanners.bannerForm.onClickUrl")}
                </Label>
                <Input
                  id="onClickUrl"
                  name="onClickUrl"
                  placeholder={t(
                    "components.screenBanners.bannerForm.onClickUrlPlaceholder"
                  )}
                  value={formik.values.onClickUrl}
                  onChange={formik.handleChange}
                  state={formik.errors.onClickUrl ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("onClickUrl").error}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validFrom">
                  {t("components.screenBanners.bannerForm.validFrom")}
                </Label>

                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.screenBanners.bannerForm.validFromPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validFrom || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validFrom", date || null);
                  }}
                />
                {formik.errors.validFrom && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validFrom").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validUntil">
                  {t("components.screenBanners.bannerForm.validUntil")}
                </Label>
                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.screenBanners.bannerForm.validUntilPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validUntil || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validUntil", date || null);
                  }}
                />
                {formik.errors.validUntil && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validUntil").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row paddingTop="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="isVipOnly">
                  {t("components.common.programForm.isVipOnly")}
                </Label>
                <Checkbox
                  id="isVipOnly"
                  name="isVipOnly"
                  value={formik.values.isVipOnly.toString()}
                  label={""}
                  onChange={formik.handleChange}
                  checked={formik.values.isVipOnly}
                />
              </FormControl>
            </Col>
          </Row>
        </Container>

        <Row>
          <Col textAlign="end">
            <Button
              type={"reset"}
              onClick={onCancel}
              marginRight="10px"
              color="light"
            >
              {t("common.cancel")}
            </Button>
            <Button type={"submit"}>{t("common.save")}</Button>
          </Col>
        </Row>

        <FileUploadModal
          isOpen={isModalUploadOpen}
          onClose={closeModalUpload}
          onFileUploaded={handleUploadPoster}
          maxFiles={1}
          accept="image/jpeg, image/png"
        />
      </form>
    );
  }
);

export default BannerForm;
